import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp, ROLE_LABELS } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { GitBranch, AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react';

export const BRANCH_MAPPING_CHANGED = 'branchMappingChanged';
export const signalBranchMappingChanged = () => window.dispatchEvent(new Event(BRANCH_MAPPING_CHANGED));

const POLL_MS = 60_000;

// HR-only banner: users assigned to a business line that has branches but not mapped to any branch.
export function BranchUnmappedBanner({ onEditUser, embedded = false }) {
  const { api, currentUser } = useApp();
  const { pathname } = useLocation();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const isHR = (currentUser?.role || '').toLowerCase() === 'hr';

  const refresh = useCallback(async () => {
    if (!isHR || !currentUser?.id) return;
    setLoading(true);
    try { const r = await api.getBranchCompliance(currentUser.id); setData(r.data); }
    catch { /* keep last */ }
    setLoading(false);
  }, [api, currentUser?.id, isHR]);

  useEffect(() => {
    if (!isHR) return;
    refresh();
    const id = setInterval(refresh, POLL_MS);
    window.addEventListener(BRANCH_MAPPING_CHANGED, refresh);
    return () => { clearInterval(id); window.removeEventListener(BRANCH_MAPPING_CHANGED, refresh); };
  }, [isHR, refresh]);

  // The global banner steps aside on Team Management, which renders its own embedded copy.
  if (!isHR || !data || data.total_unmapped === 0) return null;
  if (!embedded && pathname === '/team') return null;

  const lines = (data.lines || []).filter(l => (l.unmapped_users || []).length > 0);
  const rows = lines.flatMap(l => l.unmapped_users.map(u => ({ ...u, business_label: l.business_label || l.business_id, branches: l.branches })));

  return (
    <>
      <div data-testid="branch-unmapped-banner" className={`${embedded ? '' : 'mb-4'} flex flex-wrap items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm`}>
        <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0" />
        <div className="flex-1 min-w-[200px]">
          <span className="font-semibold text-rose-800" data-testid="branch-unmapped-count">{data.total_unmapped} user{data.total_unmapped === 1 ? '' : 's'}</span>
          <span className="text-rose-700"> not mapped to a branch — </span>
          <span className="text-rose-600">{lines.map(l => `${l.business_label || l.business_id} (${l.unmapped_users.length})`).join(' · ')}</span>
        </div>
        <Button data-testid="branch-unmapped-view-btn" size="sm" variant="outline" onClick={() => setOpen(true)} className="gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-100">
          <GitBranch className="w-4 h-4" /> View list
        </Button>
        {!embedded && (
          <Link data-testid="branch-unmapped-open-team" to="/team" className="inline-flex items-center gap-1 text-rose-700 font-semibold hover:underline">
            Open Team Management <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="branch-unmapped-dialog" className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GitBranch className="w-5 h-5 text-rose-600" /> Users without a branch ({data.total_unmapped})</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Every user of a business line that has branches must belong to one of its branches. Map them via the user's edit dialog in Team Management.</p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Business line</TableHead><TableHead>Available branches</TableHead>
                {onEditUser && <TableHead className="text-right">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(u => (
                <TableRow key={`${u.business_label}-${u.id}`} data-testid={`branch-unmapped-row-${u.id}`}>
                  <TableCell>
                    <div className="font-medium text-slate-800">{u.name}</div>
                    <div className="text-xs text-slate-500">{u.email}{u.zone ? ` · ${u.zone}` : ''}</div>
                  </TableCell>
                  <TableCell className="text-xs capitalize">{ROLE_LABELS?.[u.role] || (u.role || '').replace(/_/g, ' ')}</TableCell>
                  <TableCell><Badge variant="outline" className="border-rose-300 text-rose-700">{u.business_label}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-600">{(u.branches || []).map(b => b.name).join(', ')}</TableCell>
                  {onEditUser && (
                    <TableCell className="text-right">
                      <Button data-testid={`branch-unmapped-map-${u.id}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setOpen(false); onEditUser(u); }}>Map branch</Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <Button variant="ghost" size="sm" onClick={refresh} disabled={loading} className="gap-1.5" data-testid="branch-unmapped-refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
            {!onEditUser && (
              <Button asChild className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="branch-unmapped-dialog-open-team">
                <Link to="/team" onClick={() => setOpen(false)}>Open Team Management</Link>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
